import React, { Fragment, useState, useEffect } from "react";
import { closeModal } from "../../store/modal/modalReducer";
import { useDispatch } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faStickyNote, faHistory } from "@fortawesome/pro-regular-svg-icons";
import { Dialog, Transition } from "@headlessui/react";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { convertToRaw, EditorState, ContentState, convertFromHTML } from "draft-js";
import draftToHtml from "draftjs-to-html";
import moment from "moment";

import ErrorBoundary from "../../containers/ErrorBoundary";
import NotificationToast from "../Helpers/NotificationToast";
import { getBooking } from "../../store/booking/bookingActions";

export default function ModalBookingNotes({ booking_id, current_notes, current_staff, onSave }) {
    const dispatch = useDispatch();
    const [open] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [history, setHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [editorState, setEditorState] = useState(() => {
        if (current_notes) {
            const blocksFromHTML = convertFromHTML(current_notes);
            const contentState = ContentState.createFromBlockArray(
                blocksFromHTML.contentBlocks,
                blocksFromHTML.entityMap
            );
            return EditorState.createWithContent(contentState);
        }
        return EditorState.createEmpty();
    });

    const onEditorStateChange = (newEditorState) => {
        setEditorState(newEditorState);
    };

    const handleSave = async () => {
        setSaving(true);
        const htmlContent = draftToHtml(convertToRaw(editorState.getCurrentContent()));
        
        // Check if content is empty (just whitespace or empty paragraph)
        const isEmpty = htmlContent.replace(/<[^>]*>/g, '').trim() === '';
        
        try {
            await axios.patch(`${process.env.REACT_APP_API_URL}booking/${booking_id}/notes`, {
                notes: isEmpty ? null : htmlContent,
                staff_id: current_staff?.id,
            });
            
            toast(<NotificationToast title="Success" message="Booking notes updated successfully" />);
            dispatch(getBooking(booking_id));
            if (onSave) onSave();
            dispatch(closeModal());
        } catch (error) {
            console.error(error);
            toast(<NotificationToast title="Error" message="Failed to update notes" type="error" />);
        } finally {
            setSaving(false);
        }
    };

    const handleClear = async () => {
        setSaving(true);
        try {
            await axios.patch(`${process.env.REACT_APP_API_URL}booking/${booking_id}/notes`, {
                notes: null,
                staff_id: current_staff?.id,
            });
            
            toast(<NotificationToast title="Success" message="Booking notes cleared" />);
            dispatch(getBooking(booking_id));
            if (onSave) onSave();
            dispatch(closeModal());
        } catch (error) {
            console.error(error);
            toast(<NotificationToast title="Error" message="Failed to clear notes" type="error" />);
        } finally {
            setSaving(false);
        }
    };

    const loadHistory = async () => {
        setLoadingHistory(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}booking/${booking_id}/notes-history`);
            setHistory(response.data);
            setShowHistory(true);
        } catch (error) {
            console.error(error);
            toast(<NotificationToast title="Error" message="Failed to load history" type="error" />);
        } finally {
            setLoadingHistory(false);
        }
    };

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog
                as="div"
                static
                className="fixed z-50 inset-0 overflow-y-auto"
                open={open}
                onClose={() => dispatch(closeModal())}
            >
                <ErrorBoundary>
                    <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0"
                            enterTo="opacity-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                        </Transition.Child>

                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
                            &#8203;
                        </span>
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
                                <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                                    <button
                                        type="button"
                                        className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-0"
                                        onClick={() => dispatch(closeModal())}
                                    >
                                        <span className="sr-only">Close</span>
                                        <FontAwesomeIcon className="h-6 w-6" aria-hidden="true" icon={faTimes} />
                                    </button>
                                </div>
                                <div className="sm:flex sm:items-start mb-4">
                                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                                        <FontAwesomeIcon className="h-5 w-5 text-yellow-600" aria-hidden="true" icon={faStickyNote} />
                                    </div>
                                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                        <Dialog.Title as="h1" className="text-lg font-medium text-gray-900">
                                            Edit Booking Notes
                                        </Dialog.Title>
                                        <p className="text-sm text-gray-500">
                                            Add notes that will be displayed above the message form for this booking.
                                        </p>
                                    </div>
                                </div>
                                
                                <div className="border rounded-lg overflow-hidden min-h-[200px]">
                                    <Editor
                                        editorState={editorState}
                                        onEditorStateChange={onEditorStateChange}
                                        editorClassName="px-3 py-2 min-h-[150px] bg-white"
                                        wrapperClassName="bg-white"
                                        toolbar={{
                                            options: ["inline", "blockType", "list", "textAlign", "colorPicker", "link"],
                                            inline: { 
                                                inDropdown: false,
                                                options: ["bold", "italic", "underline", "strikethrough"],
                                            },
                                            list: { inDropdown: true },
                                            textAlign: { inDropdown: true },
                                            link: { inDropdown: true },
                                        }}
                                    />
                                </div>

                                {/* History Section */}
                                {showHistory && history.length > 0 && (
                                    <div className="mt-4 border rounded-lg p-4 bg-gray-50 max-h-60 overflow-y-auto">
                                        <div className="flex items-center justify-between mb-3">
                                            <h3 className="text-sm font-medium text-gray-900">Note History</h3>
                                            <button
                                                type="button"
                                                className="text-xs text-gray-500 hover:text-gray-700"
                                                onClick={() => setShowHistory(false)}
                                            >
                                                Hide
                                            </button>
                                        </div>
                                        <div className="space-y-3">
                                            {history.map((item, index) => (
                                                <div key={item.id} className="border-l-2 border-yellow-400 pl-3">
                                                    <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                                                        <span className="font-medium">
                                                            {item.user?.name} {item.user?.surname}
                                                        </span>
                                                        <span>{moment(item.created_at).format("MMM D, YYYY h:mm A")}</span>
                                                    </div>
                                                    {item.new_notes && (
                                                        <div className="text-xs text-gray-700 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: item.new_notes }} />
                                                    )}
                                                    {!item.new_notes && item.old_notes && (
                                                        <div className="text-xs text-gray-500 italic">Note cleared</div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="mt-5 sm:mt-4 flex justify-between">
                                    <div className="flex space-x-2">
                                        <button
                                            type="button"
                                            className="inline-flex items-center justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
                                            onClick={loadHistory}
                                            disabled={loadingHistory}
                                        >
                                            <FontAwesomeIcon icon={faHistory} className="mr-2 h-4 w-4" />
                                            {loadingHistory ? "Loading..." : "History"}
                                        </button>
                                        {current_notes && (
                                            <button
                                                type="button"
                                                className="inline-flex justify-center rounded-md border border-red-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-red-700 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                                                onClick={handleClear}
                                                disabled={saving}
                                            >
                                                Clear Notes
                                            </button>
                                        )}
                                    </div>
                                    <div className="flex space-x-3">
                                        <button
                                            type="button"
                                            className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                            onClick={() => dispatch(closeModal())}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-sm font-medium text-white hover:bg-primary_hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
                                            onClick={handleSave}
                                            disabled={saving}
                                        >
                                            {saving ? "Saving..." : "Save Notes"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </Transition.Child>
                    </div>
                </ErrorBoundary>
            </Dialog>
        </Transition.Root>
    );
}
